import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PhoneCall, ArrowUp, ArrowDown, X, AlertTriangle, CheckCircle2, Calendar, UserCheck, Repeat, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { fmtETDate } from '../../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENTS = [
  { key: 'service',          label: 'Service' },
  { key: 'new_construction', label: 'New Construction' },
];

const OnCall = () => {
  const [department, setDepartment] = useState('service');
  const [snapshot, setSnapshot] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [draftOrder, setDraftOrder] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [snap, emps] = await Promise.all([
        axios.get(`${API}/admin/on-call/rotation/${department}`, { withCredentials: true }),
        axios.get(`${API}/admin/employees`, { withCredentials: true }),
      ]);
      setSnapshot(snap.data);
      setEmployees(emps.data || []);
      setDraftOrder((snap.data.order || []).map((o) => o.user_id));
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rotation');
    }
  }, [department]);

  useEffect(() => { load(); }, [load]);

  const employeesById = Object.fromEntries(employees.map((e) => [e.id, e]));
  const inRotation = new Set(draftOrder);
  const addable = employees.filter((e) => !inRotation.has(e.id) && e.role !== 'customer');

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= draftOrder.length) return;
    const next = [...draftOrder];
    [next[idx], next[target]] = [next[target], next[idx]];
    setDraftOrder(next); setDirty(true);
  };
  const removeAt = (idx) => { setDraftOrder(draftOrder.filter((_, i) => i !== idx)); setDirty(true); };
  const addEmp = (uid) => { if (uid && !inRotation.has(uid)) { setDraftOrder([...draftOrder, uid]); setDirty(true); } };

  const saveRotation = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/on-call/rotation/${department}`,
        { order: draftOrder, anchor_date: snapshot?.anchor_date || undefined },
        { withCredentials: true });
      setSnapshot(data);
      setDirty(false);
      toast.success('Rotation saved — system will auto-advance each Saturday');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const overrideCurrent = async (uid) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/on-call/rotation/${department}/override`,
        { user_id: uid, period_start: snapshot.current.period_start },
        { withCredentials: true });
      setSnapshot(data);
      toast.success('Current on-call overridden');
    } catch (e) { toast.error(e.response?.data?.detail || 'Override failed'); }
  };
  const clearOverride = async (ps) => {
    try {
      const { data } = await axios.delete(
        `${API}/admin/on-call/rotation/${department}/override?period_start=${ps}`,
        { withCredentials: true });
      setSnapshot(data);
      toast.success('Override cleared');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const skipPeriod = async (uid, ps) => {
    try {
      const { data } = await axios.post(`${API}/admin/on-call/rotation/${department}/skip`,
        { user_id: uid, period_start: ps }, { withCredentials: true });
      setSnapshot(data);
      toast.success('Employee skipped for that rotation');
    } catch (e) { toast.error(e.response?.data?.detail || 'Skip failed'); }
  };

  const Card = ({ kind, period }) => {
    const isOverride = period.source === 'override';
    const isVacant = period.source === 'vacant';
    return (
      <div data-testid={`oncall-card-${kind}`} className={`bg-[#141414] border rounded-md p-4 ${isVacant ? 'border-red-700' : 'border-[#262626]'}`}>
        <p className="text-[#525252] text-[10px] uppercase tracking-wide">{kind === 'current' ? 'Current On-Call' : 'Next Up'}</p>
        <p className="text-white text-lg font-bold mt-1" data-testid={`oncall-${kind}-name`}>{period.user_name || '—'}</p>
        <p className="text-[#A3A3A3] text-xs mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtETDate(period.period_start)} – {fmtETDate(period.period_end)}</p>
        {isOverride && (
          <div className="mt-2 flex items-center gap-2">
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-700 text-[10px] uppercase">Override</Badge>
            <button onClick={() => clearOverride(period.period_start)} className="text-amber-300 text-[10px] underline">Clear</button>
          </div>
        )}
        {isVacant && (
          <Badge className="bg-red-500/15 text-red-400 border-red-700 text-[10px] uppercase mt-2">No Eligible Employee</Badge>
        )}
      </div>
    );
  };

  return (
    <div data-testid="on-call-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-2">
            <PhoneCall className="w-6 h-6 text-[#D4AF37]" /> On-Call Rotation
          </h2>
          <p className="text-[#A3A3A3] text-sm mt-1">Set the rotation order once. The system auto-advances each Saturday — no manual dates required.</p>
        </div>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger data-testid="oncall-dept-select" className="w-56 bg-[#141414] border-[#262626] text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            {DEPARTMENTS.map((d) => <SelectItem key={d.key} value={d.key} className="text-white">{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {snapshot && snapshot.rotation_configured && snapshot.current && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Card kind="current" period={snapshot.current} />
          {snapshot.next && <Card kind="next" period={snapshot.next} />}
        </div>
      )}

      {snapshot && !snapshot.rotation_configured && (
        <div data-testid="oncall-empty-state" className="bg-[#141414] border border-amber-700/40 rounded-md p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white">No rotation list for <span className="text-[#D4AF37]">{department.replace('_',' ')}</span> yet.</p>
            <p className="text-[#A3A3A3] text-xs mt-1">Add employees to the rotation below, then click <span className="text-white">Save Rotation</span>. The system will auto-assign starting this Saturday.</p>
          </div>
        </div>
      )}

      {/* Manage Rotation list */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3" data-testid="oncall-manage-rotation">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Manage Rotation</p>
            <p className="text-[#A3A3A3] text-xs mt-0.5">Use up/down arrows to set the rotation order. New employees join at the end of the list.</p>
          </div>
          <Button data-testid="oncall-save-rotation" onClick={saveRotation} disabled={!dirty || saving} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save Rotation'}
          </Button>
        </div>
        <div className="space-y-1.5">
          {draftOrder.length === 0 && <p className="text-[#525252] text-sm">No employees in rotation yet — add one below.</p>}
          {draftOrder.map((uid, idx) => {
            const emp = employeesById[uid] || { name: '(unknown)' };
            // iter76 — Match Employees page convention: only treat an explicit `active === false`
            // as inactive. Older employee records may not have the `active` field set at all
            // (undefined → still active). This fixes the bug where Adam Cordle showed
            // "INACTIVE (SKIPPED)" in the rotation while showing ACTIVE on the Employees page.
            const inactive = emp.active === false;
            return (
              <div key={uid} data-testid={`oncall-order-row-${idx}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                <span className="text-[#525252] text-xs w-6 text-right">{idx + 1}.</span>
                <span className="text-white flex-1 truncate">{emp.name} {inactive && <Badge className="bg-red-500/15 text-red-400 border-red-700 text-[9px] uppercase ml-2">Inactive (skipped)</Badge>}</span>
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`oncall-up-${idx}`} className="text-[#A3A3A3] hover:text-[#D4AF37] disabled:opacity-30 p-1"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === draftOrder.length - 1} data-testid={`oncall-down-${idx}`} className="text-[#A3A3A3] hover:text-[#D4AF37] disabled:opacity-30 p-1"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => removeAt(idx)} data-testid={`oncall-remove-${idx}`} className="text-[#A3A3A3] hover:text-red-400 p-1"><X className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
        {addable.length > 0 && (
          <Select value="" onValueChange={addEmp}>
            <SelectTrigger data-testid="oncall-add-employee" className="bg-[#0A0A0A] border-[#262626] text-white">
              <SelectValue placeholder="+ Add employee to rotation" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {addable.map((e) => <SelectItem key={e.id} value={e.id} className="text-white">{e.name} {e.role && <span className="text-[#525252] ml-1">— {e.role}</span>}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Quick override + Upcoming */}
      {snapshot && snapshot.rotation_configured && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div data-testid="oncall-override-panel" className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-2">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Override Current On-Call</p>
            <p className="text-[#A3A3A3] text-xs">Pin a different employee to this Saturday's period. The auto-rotation resumes the following Saturday.</p>
            <Select value="" onValueChange={overrideCurrent}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white">
                <SelectValue placeholder="Pick an employee" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {employees.filter((e) => e.role !== 'customer').map((e) => <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div data-testid="oncall-upcoming-panel" className="bg-[#141414] border border-[#262626] rounded-md p-5">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold flex items-center gap-1"><Repeat className="w-3 h-3" /> Upcoming Rotations</p>
            <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {(snapshot.upcoming || []).map((p) => (
                <div key={p.period_start} data-testid={`oncall-upcoming-${p.period_start}`} className="flex items-center justify-between text-xs border-l-2 pl-2 py-1" style={{ borderColor: p.source === 'override' ? '#D4AF37' : (p.source === 'vacant' ? '#dc2626' : '#262626') }}>
                  <div>
                    <p className="text-white">{p.user_name || '—'} {p.source === 'override' && <Badge className="bg-amber-500/15 text-amber-400 border-amber-700 text-[9px] uppercase ml-1">Override</Badge>}</p>
                    <p className="text-[#525252] text-[10px]">{fmtETDate(p.period_start)} – {fmtETDate(p.period_end)}</p>
                  </div>
                  {p.user_id && (
                    <button onClick={() => skipPeriod(p.user_id, p.period_start)} className="text-[#A3A3A3] text-[10px] hover:text-amber-300">Skip</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnCall;
